import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';

const Section: React.FC<{ icon: string; title: string; sub: string; borderColor: string; bgColor: string; children: React.ReactNode }> = ({ icon, title, sub, borderColor, bgColor, children }) => (
  <div style={{ marginBottom: '48px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '14px', borderBottom: `2px solid ${borderColor}` }}>
      <div style={{ width: '42px', height: '42px', borderRadius: '11px', background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>{icon}</div>
      <div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--gray-800)' }}>{title}</h2>
        <p style={{ fontSize: '0.82rem', color: 'var(--gray-400)' }}>{sub}</p>
      </div>
    </div>
    <div className="cards-grid">{children}</div>
  </div>
);

interface CardData { icon: string; iconCls?: string; title: string; desc: string; tag: string; tagCls?: string; link?: string; }
const Card: React.FC<CardData> = ({ icon, iconCls, title, desc, tag, tagCls, link }) => {
  const cardContent = (
    <div className="card">
      <div className={`card-icon${iconCls ? ' ' + iconCls : ''}`}>{icon}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
      <span className={`card-tag${tagCls ? ' ' + tagCls : ''}`}>{tag}</span>
    </div>
  );

  return link ? <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>{cardContent}</Link> : cardContent;
};

const ProgramsPage: React.FC = () => (
  <Layout>
    <div className="page-hero">
      <span className="section-tag">Our Programs</span>
      <h1>Learn. Build. <span className="text-orange">Achieve.</span></h1>
      <p>Expert-designed curricula across cloud, programming, system design, and architecture domains.</p>
    </div>

    <div className="section">
      <div className="container">
        <Section icon="☁️" title="AWS Cloud Programs" sub="3 courses · Beginner to Expert" borderColor="var(--blue-soft)" bgColor="var(--blue-pale)">
          <Card icon="🌱" title="AWS Cloud Practitioner (CCP)" desc="Cloud concepts, AWS core services, billing, security. Ideal for beginners and non-technical roles." tag="Beginner · 4 weeks" link="/aws-ccp" />
          <Card icon="🏗️" title="AWS Solutions Architect Associate (SAA)" desc="EC2, S3, VPC, RDS, Lambda, CloudFormation, IAM. Design production-grade AWS architectures." tag="Intermediate · 8 weeks" />
          <Card icon="🔧" title="AWS Developer Associate (DVA)" desc="DynamoDB, API Gateway, Lambda, CI/CD, SAM, Elastic Beanstalk. Build serverless applications." tag="Intermediate · 8 weeks" />
          <Card icon="🏆" iconCls="orange" title="AWS Solutions Architect Professional (SAP)" desc="Multi-account governance, advanced networking, migration, HA/DR and cost optimization. Prepare for SAP-C02 with a full guide and scenario practice." tag="Advanced · 12 weeks" tagCls="orange" link="/aws-sap" />
        </Section>

        <Section icon="💻" title="Programming Languages" sub="5 languages · All levels" borderColor="var(--orange-soft)" bgColor="var(--orange-pale)">
          <Card icon="🐍" iconCls="orange" title="Python Programming" desc="Scripting, OOP, async, FastAPI, Django, automation, data pipelines, and cloud boto3 scripting." tag="All Levels · 6 weeks" tagCls="orange" />
          <Card icon="☕" iconCls="orange" title="Java Development" desc="Core Java, Spring Boot, Hibernate, REST APIs, microservices, JVM tuning, enterprise patterns." tag="All Levels · 8 weeks" tagCls="orange" />
          <Card icon="🔷" iconCls="orange" title=".NET / C# Development" desc="ASP.NET Core, EF Core, CQRS, gRPC, Blazor, MAUI, and Azure cloud integration." tag="All Levels · 8 weeks" tagCls="orange" />
          <Card icon="⚡" iconCls="orange" title="C & C++ Programming" desc="Pointers, memory management, STL, system programming, competitive programming foundations." tag="All Levels · 6 weeks" tagCls="orange" />
        </Section>

        <Section icon="⚙️" title="System Design & Problem Solving" sub="2 courses · Intermediate to Expert" borderColor="var(--blue-soft)" bgColor="var(--blue-pale)">
          <Card icon="⚙️" title="System Design Mastery" desc="CAP theorem, sharding, replication, event-driven design, API gateways, message queues, distributed transactions." tag="Advanced · 10 weeks" />
          <Card icon="🧩" title="Data Structures & Algorithms" desc="Arrays, trees, graphs, dynamic programming, sorting, backtracking. FAANG-level interview preparation." tag="Intermediate · 8 weeks" />
        </Section>

        <Section icon="🤖" title="AI & Machine Learning" sub="3 courses · Beginner to Expert" borderColor="var(--orange-soft)" bgColor="var(--orange-pale)">
          <Card icon="🤖" iconCls="orange" title="ML Foundations" desc="Supervised & unsupervised learning, regression, classification, clustering, model evaluation, scikit-learn." tag="Beginner · 6 weeks" tagCls="orange" />
          <Card icon="🧠" iconCls="orange" title="Deep Learning & Neural Networks" desc="CNNs, RNNs, Transformers, PyTorch & TensorFlow. Build and deploy production-ready deep learning models." tag="Intermediate · 10 weeks" tagCls="orange" />
          <Card icon="✨" iconCls="orange" title="Generative AI & LLMs" desc="Prompt engineering, RAG pipelines, fine-tuning LLMs, LangChain, vector databases, AI application development." tag="Advanced · 8 weeks" tagCls="orange" />
        </Section>
      </div>
    </div>

    <div style={{ background: 'var(--orange-pale)', padding: '56px 6%', textAlign: 'center', borderTop: '1px solid var(--orange-soft)' }}>
      <h2 className="section-h2">Ready to Start Learning?</h2>
      <p className="section-sub">Book a free 30-minute consultation with one of our instructors.</p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
        <Link to="/request" className="btn btn-orange btn-lg">Request Session</Link>
        <Link to="/practice-exam" className="btn btn-outline-blue btn-lg">Try Free Exam</Link>
      </div>
    </div>
  </Layout>
);

export default ProgramsPage;
